import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './SealedCartonsPanel.css';

/**
 * Sealed at the taper — production counting from the sealer camera, on the
 * Packaging screen.
 *
 * Per SKU: sealed cartons and units today against the open work order's plan,
 * cartons per hour, and a "Suggested build" the operator confirms before the
 * assembly build posts (through the existing native build path). Cartons the
 * server could not tie to a work order are listed under "Needs a work order"
 * with a picker. The camera never posts on its own.
 *
 * Endpoints are public station paths (no login); the operator comes from the
 * kiosk PIN login and is stored with the confirmation.
 */

const BASE = '/api/key-account-check/station/production';

function fmt(n) {
  return Number(n || 0).toLocaleString();
}

function Progress({ pct }) {
  const p = pct == null ? 0 : Math.max(0, Math.min(100, pct));
  return (
    <div className="sct-progress" aria-label={`${p}% of plan sealed`}>
      <div className="sct-progress-bar" style={{ width: `${p}%` }} />
    </div>
  );
}

function WorkOrderPicker({ sku, value, onPick, onCancel }) {
  const [list, setList] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    axios.get(BASE + '/work-orders', { params: sku ? { sku } : {} })
      .then((r) => { if (live) setList(r.data.workOrders || []); })
      .catch((e) => { if (live) setErr(e.response?.data?.error || e.message); });
    return () => { live = false; };
  }, [sku]);
  return (
    <div className="sct-picker">
      <div className="sct-picker-title">{sku ? `Open work orders for ${sku}` : 'Open work orders'}</div>
      {err && <div className="sct-error">{err}</div>}
      {list === null && !err && <div className="sct-muted">Loading...</div>}
      {list && list.length === 0 && <div className="sct-muted">No open work order{sku ? ` for ${sku}` : ''}.</div>}
      <div className="sct-picker-list">
        {(list || []).map((w) => (
          <button
            key={`${w.source}-${w.id}`}
            type="button"
            className={`sct-picker-row ${value && value.id === w.id ? 'is-selected' : ''}`}
            onClick={() => onPick(w)}
          >
            <span className="sct-picker-wo">WO {w.tranid || w.id.slice(0, 8)}</span>
            <span className="sct-picker-sku">{w.sku || '?'}</span>
            <span className="sct-picker-qty">{fmt(w.quantity_built)} / {fmt(w.quantity)} built</span>
            <span className={`sct-tag sct-tag-${w.source}`}>{w.source === 'native' ? 'Run' : 'NetSuite'}</span>
          </button>
        ))}
      </div>
      <button type="button" className="sct-btn sct-btn-ghost" onClick={onCancel}>Cancel</button>
    </div>
  );
}

function ConfirmDialog({ suggestion, operator, station, onClose, onDone }) {
  const [cases, setCases] = useState(suggestion.cases);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [refused, setRefused] = useState(null);
  const over = suggestion.remaining != null && Number(cases) > suggestion.remaining;
  const more = Number(cases) > suggestion.cases;
  const needsReason = over || more;

  const submit = async () => {
    setBusy(true); setErr(''); setRefused(null);
    try {
      const r = await axios.post(BASE + '/confirm-build', {
        workOrderId: suggestion.workOrder.id,
        workOrderSource: suggestion.workOrder.source,
        cases: Number(cases),
        operator,
        station,
        sealedCartonIds: suggestion.sealedCartonIds,
        override: needsReason,
        reason: needsReason ? reason : undefined,
      });
      onDone(r.data);
    } catch (e) {
      const d = e.response?.data;
      if (e.response?.status === 409 && d) setRefused(d.reasons || [d.error]);
      else setErr(d?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sct-modal-backdrop" role="dialog" aria-modal="true">
      <div className="sct-modal">
        <div className="sct-modal-title">Confirm assembly build</div>
        <div className="sct-modal-sub">
          {suggestion.sku} against WO {suggestion.workOrder.tranid || suggestion.workOrder.id.slice(0, 8)}
        </div>
        <div className="sct-modal-grid">
          <div><span className="sct-k">Sealed, not yet built</span><span className="sct-v">{fmt(suggestion.cases)} cases</span></div>
          <div><span className="sct-k">Units</span><span className="sct-v">{suggestion.caseQty ? fmt(Number(cases) * suggestion.caseQty) : '-'}</span></div>
          <div><span className="sct-k">Plan</span><span className="sct-v">{suggestion.planned != null ? fmt(suggestion.planned) : '-'}</span></div>
          <div><span className="sct-k">Already built</span><span className="sct-v">{suggestion.alreadyBuilt != null ? fmt(suggestion.alreadyBuilt) : '-'}</span></div>
          <div><span className="sct-k">Remaining on WO</span><span className="sct-v">{suggestion.remaining != null ? fmt(suggestion.remaining) : '-'}</span></div>
          <div><span className="sct-k">Confirmed by</span><span className="sct-v">{operator ? (operator.display_name || operator.first_name || operator.name) : 'scan badge'}</span></div>
        </div>
        <label className="sct-field">
          <span>Cases to build</span>
          <input type="number" min="1" value={cases} onChange={(e) => setCases(e.target.value)} className="sct-input" />
        </label>
        {needsReason && (
          <label className="sct-field">
            <span>{more ? 'More than the camera counted. ' : ''}{over ? 'More than the work order has left. ' : ''}Reason for the override</span>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} className="sct-input" placeholder="Why the count differs" />
          </label>
        )}
        {refused && (
          <div className="sct-refused">
            <div className="sct-refused-title">Not posted</div>
            <ul>{refused.map((r, i) => <li key={i}>{r}</li>)}</ul>
          </div>
        )}
        {err && <div className="sct-error">{err}</div>}
        <div className="sct-modal-actions">
          <button type="button" className="sct-btn sct-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="sct-btn sct-btn-primary"
            onClick={submit}
            disabled={busy || !operator || !(Number(cases) > 0) || (needsReason && !reason.trim())}
          >
            {busy ? 'Posting...' : `Post build of ${fmt(cases)} cases`}
          </button>
        </div>
        <div className="sct-muted sct-small">Posts through the native assembly build. Nothing posts until this button is pressed.</div>
      </div>
    </div>
  );
}

export default function SealedCartonsPanel({ station, operator }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [picker, setPicker] = useState(null); // { kind: 'select' } | { kind: 'assign', ids, sku }
  const [confirm, setConfirm] = useState(null); // suggestion
  const [flash, setFlash] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(BASE + '/summary', { params: { station } });
      setData(r.data);
      setErr('');
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  }, [station]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const say = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 4000); };

  const selectWorkOrder = async (wo) => {
    try {
      await axios.post(BASE + '/select', { station, workOrderId: wo ? wo.id : null, workOrderSource: wo ? wo.source : null, operator });
      setPicker(null);
      say(wo ? `Station set to WO ${wo.tranid || ''} (${wo.sku})` : 'Station work order cleared');
      load();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
  };

  const assign = async (wo) => {
    try {
      const r = await axios.post(BASE + '/assign', { sealedCartonIds: picker.ids, workOrderId: wo.id, workOrderSource: wo.source, operator });
      setPicker(null);
      say(`${r.data.assigned} carton${r.data.assigned === 1 ? '' : 's'} assigned to WO ${wo.tranid || ''}`);
      load();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
  };

  const suggest = async (s) => {
    try {
      const r = await axios.post(BASE + '/suggest-build', { workOrderId: s.workOrder.id, workOrderSource: s.workOrder.source });
      const sug = r.data.suggestion;
      if (!sug.cases) { say('Nothing sealed on that work order since the last build'); return; }
      setConfirm({ ...sug, writesEnabled: r.data.writesEnabled });
    } catch (e) { setErr(e.response?.data?.error || e.message); }
  };

  const skus = (data && data.skus) || [];
  const unassigned = (data && data.unassigned) || [];
  const selected = data && data.selectedWorkOrder;

  return (
    <section className="sct">
      <div className="sct-head">
        <div>
          <div className="sct-title">Sealed at the taper</div>
          <div className="sct-sub">{station ? `Station ${station}` : 'All stations'} · today · counted by the sealer camera</div>
        </div>
        <div className="sct-head-right">
          <div className="sct-selected">
            {selected
              ? <span>Counting against <strong>WO {selected.tranid}</strong> {selected.sku}</span>
              : <span className="sct-muted">No work order selected: cartons count against the oldest open work order for their SKU</span>}
          </div>
          <button type="button" className="sct-btn sct-btn-ghost" onClick={() => setPicker({ kind: 'select' })}>Select work order</button>
          {selected && <button type="button" className="sct-btn sct-btn-ghost" onClick={() => selectWorkOrder(null)}>Clear</button>}
        </div>
      </div>

      {flash && <div className="sct-flash">{flash}</div>}
      {err && <div className="sct-error">{err}</div>}

      {picker && picker.kind === 'select' && (
        <WorkOrderPicker value={selected} onPick={selectWorkOrder} onCancel={() => setPicker(null)} />
      )}
      {picker && picker.kind === 'assign' && (
        <WorkOrderPicker sku={picker.sku} onPick={assign} onCancel={() => setPicker(null)} />
      )}

      {data && skus.length === 0 && unassigned.length === 0 && (
        <div className="sct-empty">No cartons sealed yet today. Every carton the camera sees will show here.</div>
      )}

      <div className="sct-cards">
        {skus.map((s) => (
          <div key={s.sku} className="sct-card">
            <div className="sct-card-head">
              <div>
                <div className="sct-sku">{s.sku}</div>
                <div className="sct-muted">{s.caseQty ? `case of ${s.caseQty}` : 'case size unknown'}</div>
              </div>
              <div className="sct-rate">{s.cartonsPerHour} <span>cartons / hr</span></div>
            </div>
            <div className="sct-nums">
              <div><span className="sct-big">{fmt(s.cartons)}</span><span className="sct-k">cartons sealed</span></div>
              <div><span className="sct-big">{fmt(s.units)}</span><span className="sct-k">units</span></div>
              <div><span className="sct-big">{fmt(s.unbuiltCartons)}</span><span className="sct-k">not yet built</span></div>
            </div>
            {s.workOrder ? (
              <div className="sct-wo">
                <div className="sct-wo-line">
                  <span>WO {s.workOrder.tranid} <span className={`sct-tag sct-tag-${s.workOrder.source}`}>{s.workOrder.source === 'native' ? 'Run' : 'NetSuite'}</span></span>
                  <span>{fmt(s.workOrder.alreadyBuilt + s.workOrder.unbuiltOnWo)} of {fmt(s.workOrder.planned)} cases</span>
                </div>
                <Progress pct={s.workOrder.pctSealed} />
                <div className="sct-wo-foot">
                  <span className="sct-muted">{fmt(s.workOrder.alreadyBuilt)} built earlier · {fmt(s.workOrder.unbuiltOnWo)} sealed since · {fmt(Math.max(0, s.workOrder.remaining - s.workOrder.unbuiltOnWo))} still to seal</span>
                  <button
                    type="button"
                    className="sct-btn sct-btn-primary"
                    disabled={!s.workOrder.unbuiltOnWo}
                    onClick={() => suggest(s)}
                  >
                    Suggested build: {fmt(s.workOrder.unbuiltOnWo)} cases of {s.sku} against WO {s.workOrder.tranid}
                  </button>
                </div>
              </div>
            ) : (
              <div className="sct-wo sct-wo-none">
                <span>No open work order for {s.sku}.</span>
                <button type="button" className="sct-btn sct-btn-ghost" onClick={() => setPicker({ kind: 'assign', sku: s.sku, ids: unassigned.filter((u) => u.sku === s.sku).map((u) => u.id) })}>Assign to work order</button>
              </div>
            )}
            {s.unassignedCartons > 0 && s.workOrder && (
              <div className="sct-warn">
                {fmt(s.unassignedCartons)} carton{s.unassignedCartons === 1 ? '' : 's'} of {s.sku} have no work order.
                <button type="button" className="sct-btn sct-btn-ghost" onClick={() => setPicker({ kind: 'assign', sku: s.sku, ids: unassigned.filter((u) => u.sku === s.sku).map((u) => u.id) })}>Assign</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {unassigned.filter((u) => !u.sku).length > 0 && (
        <div className="sct-warn sct-warn-block">
          <div className="sct-warn-title">Needs a work order</div>
          <div className="sct-muted">{unassigned.filter((u) => !u.sku).length} carton{unassigned.filter((u) => !u.sku).length === 1 ? '' : 's'} had no readable SKU barcode. Check the carton label printer, then assign them.</div>
          <button type="button" className="sct-btn sct-btn-ghost" onClick={() => setPicker({ kind: 'assign', sku: null, ids: unassigned.filter((u) => !u.sku).map((u) => u.id) })}>Assign to work order</button>
        </div>
      )}

      {confirm && (
        <ConfirmDialog
          suggestion={confirm}
          operator={operator}
          station={station}
          onClose={() => setConfirm(null)}
          onDone={(r) => {
            setConfirm(null);
            say(r.build ? `Build ${r.build.number || ''} posted: ${fmt(r.cases)} cases` : `Confirmed ${fmt(r.cases)} cases`);
            load();
          }}
        />
      )}
    </section>
  );
}
